import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../api";
import PublicNavBar from "../components/PublicNavBar";
import PublicFooter from "../components/PublicFooter";
import { GraduationCap, Briefcase, BookOpen, Rocket, ArrowRight, Mail, Lock, User2, Building2 } from "lucide-react";
import LinkedInButton from "../components/LinkedInButton";

const ROLES = [
  {
    key: "student",
    label: "Student",
    icon: GraduationCap,
    color: "#006CFF",
    accent: "#3E7CFF",
    tagline: "Apply to internships, learn at CoE labs, earn verified credentials.",
    perks: ["Verified profile", "CoE badges", "Auto-matched jobs"],
    nameLabel: "Your full name",
    namePlaceholder: "Arjun Rao",
    orgRequired: false,
  },
  {
    key: "working_pro",
    label: "Working Professional",
    shortLabel: "Working Pro",
    icon: Rocket,
    color: "#0052CC",
    accent: "#00B8D4",
    tagline: "Land lateral roles, upskill at CoE labs, earn verified credentials.",
    perks: ["Lateral roles", "Verified profile", "Salary insights"],
    nameLabel: "Your full name",
    namePlaceholder: "Priya Sharma",
    orgRequired: false,
  },
  {
    key: "company",
    label: "Company",
    icon: Briefcase,
    color: "#9933FF",
    accent: "#C26BFF",
    tagline: "Post roles, filter by CoE badges, hire faster.",
    perks: ["Skill-verified talent", "AI-matched applicants", "Unlimited postings"],
    nameLabel: "Company name",
    namePlaceholder: "Acme Corp",
    orgRequired: true,
  },
  {
    key: "institution",
    label: "Institution",
    icon: BookOpen,
    color: "#0099FF",
    accent: "#56C3FF",
    tagline: "Onboard your batch, drive placements, track outcomes.",
    perks: ["Campus dashboard", "Placement reports", "Recruiter network"],
    nameLabel: "Institution name",
    namePlaceholder: "Tech Catalyst Pune",
    orgRequired: true,
  },
];

const INSTITUTION_TYPES = [
  { value: "university", label: "University" },
  { value: "college", label: "College" },
  { value: "institute", label: "Institute" },
  { value: "polytechnic", label: "Polytechnic" },
];

export default function Signup() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { register } = useAuth();

  // Legacy URL: /signup?role=student&track=professional OR ?role=professional OR
  // ?role=institute → snap to the new canonical role tiles so deep links keep working.
  const rawRole = sp.get("role");
  const rawTrack = (sp.get("track") || "").toLowerCase();
  let resolvedKey = rawRole;
  if (rawRole === "student" && rawTrack === "professional") resolvedKey = "working_pro";
  if (rawRole === "professional") resolvedKey = "working_pro";
  if (rawRole === "institute") resolvedKey = "institution";
  const initial = ROLES.find((r) => r.key === resolvedKey) || ROLES[0];
  const [role, setRole] = useState(initial);
  const view = role; // kept for legacy template references — single source of truth now
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    current_company: "", current_role: "", years_of_experience: "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // Institutional sub-fields
  const [instType, setInstType] = useState("institute");
  const [aff, setAff] = useState("");
  const [universities, setUniversities] = useState([]);
  // Company dedup: typeahead matches + which one (if any) user selected to
  // join. When `joinCompany` is set, submit will use join_company_code and
  // link the recruiter to the existing org instead of creating a new one.
  const [companyMatches, setCompanyMatches] = useState([]);
  const [joinCompany, setJoinCompany] = useState(null);
  const [showMatches, setShowMatches] = useState(false);

  useEffect(() => {
    if (role.key === "institution") {
      api.get("/institutions/universities").then(r => setUniversities(r.data || []));
    }
  }, [role.key]);

  // Debounced company typeahead — only runs on the Company tile.
  useEffect(() => {
    if (role.key !== "company") { setCompanyMatches([]); return; }
    if (joinCompany) return; // don't re-search after a pick
    const q = (form.name || "").trim();
    if (q.length < 2) { setCompanyMatches([]); return; }
    const t = setTimeout(() => {
      api.get(`/companies/search?q=${encodeURIComponent(q)}`)
        .then(r => setCompanyMatches(Array.isArray(r.data) ? r.data : []))
        .catch(() => setCompanyMatches([]));
    }, 220);
    return () => clearTimeout(t);
  }, [form.name, role.key, joinCompany]);

  const upd = (k) => (e) => {
    // Typing in the Company name field cancels any previous "join" selection.
    if (k === "name" && role.key === "company") setJoinCompany(null);
    setForm({ ...form, [k]: e.target.value });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const payload = {
        email: form.email,
        password: form.password,
        role: role.key,
        name: form.name,
        organization: role.orgRequired ? form.name : undefined,
      };
      if (role.key === "institution") {
        payload.institution_type = instType;
        if (instType !== "university") payload.affiliated_university = aff || undefined;
      }
      if (role.key === "working_pro") {
        if (form.current_company)     payload.current_company = form.current_company;
        if (form.current_role)        payload.current_role = form.current_role;
        if (form.years_of_experience) payload.years_of_experience = Number(form.years_of_experience);
      }
      if (role.key === "company" && joinCompany?.company_code) {
        // Recruiter is joining an existing company — link by code instead
        // of creating a new company doc.
        payload.join_company_code = joinCompany.company_code;
        payload.organization = joinCompany.name;
      }
      const r = await register(payload);
      if (!r.ok) { setError(r.error); return; }
      nav(`/${r.user.role}/dashboard`, { replace: true });
    } finally {
      setBusy(false);
    }
  };

  const onGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL — Emergent Auth handles role detection on first login
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const Icon = role.icon;

  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <PublicNavBar primaryCta={{ to: "/login", label: "Log in" }} />

      <main className="flex-1 max-w-5xl w-full mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="text-center max-w-xl mx-auto">
          <span className="eyebrow" style={{ color: role.color }}>Create your account</span>
          <h1 className="font-display font-black text-3xl md:text-5xl mt-2 leading-tight" data-testid="signup-heading">
            Join in <span style={{ color: role.color }}>30 seconds</span>.
          </h1>
          <p className="text-muted text-sm mt-2">Pick your role, sign up with Google or email — done.</p>
        </div>

        {/* Role picker — four rich tiles (2-col on mobile, 4-col desktop) */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-8" data-testid="signup-role-picker">
          {ROLES.map((r) => {
            const active = r.key === role.key;
            const RIcon = r.icon;
            return (
              <button
                key={r.key}
                type="button"
                onClick={() => setRole(r)}
                data-testid={`signup-role-${r.key}`}
                aria-pressed={active}
                className="group relative text-left rounded-2xl p-4 sm:p-5 bg-white overflow-hidden transition-all duration-300 hover:-translate-y-1 active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                style={{
                  // 2-tone border: keep a single bold border-color but fake a
                  // gradient edge with two stacked box-shadows + transparent border.
                  border: `2px solid ${active ? "transparent" : "var(--rule)"}`,
                  boxShadow: active
                    ? `0 0 0 2px ${r.color}, 0 18px 36px -20px ${r.color}80, inset 0 0 0 1px ${r.accent}30`
                    : "0 1px 2px rgba(15,23,42,0.04)",
                  background: active
                    ? `linear-gradient(155deg, ${r.color}10 0%, white 55%, ${r.accent}08 100%)`
                    : "white",
                }}
              >
                {/* Decorative gradient blob — only visible on hover/active */}
                <span
                  aria-hidden="true"
                  className={`pointer-events-none absolute -top-10 -right-10 w-24 h-24 rounded-full blur-2xl transition-opacity duration-500 ${active ? "opacity-60" : "opacity-0 group-hover:opacity-40"}`}
                  style={{ background: `radial-gradient(circle, ${r.accent} 0%, transparent 70%)` }}
                />

                <div className="relative flex items-center gap-3">
                  <div
                    className="w-11 h-11 rounded-xl grid place-items-center transition-transform duration-500 group-hover:scale-110 group-hover:rotate-[-4deg]"
                    style={{
                      background: active
                        ? `linear-gradient(135deg, ${r.color} 0%, ${r.accent} 100%)`
                        : `${r.color}15`,
                      color: active ? "#FFFFFF" : r.color,
                      boxShadow: active ? `0 8px 18px -10px ${r.color}` : "none",
                    }}
                  >
                    <RIcon size={20} strokeWidth={2.2} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div
                      className="font-display font-extrabold text-base sm:text-lg leading-tight truncate"
                      style={{ color: active ? r.color : "var(--ink)" }}
                    >
                      {r.shortLabel || r.label}
                    </div>
                  </div>
                </div>

                <p
                  className="relative text-[11.5px] sm:text-xs leading-snug mt-3 line-clamp-3"
                  style={{ color: active ? "var(--ink)" : "var(--muted)" }}
                >
                  {r.tagline}
                </p>

                <div className="relative mt-3 flex flex-wrap gap-1">
                  {r.perks.slice(0, 2).map((p) => (
                    <span
                      key={p}
                      className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded transition-colors"
                      style={{
                        background: active ? `${r.color}18` : `${r.color}10`,
                        color: r.color,
                      }}
                    >
                      {p}
                    </span>
                  ))}
                </div>
              </button>
            );
          })}
        </div>

        {/* Form — only the bare essentials */}
        <div className="mt-8 grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3">
            <div className="bg-white border rounded-3xl shadow-[0_20px_60px_-30px_rgba(0,40,168,0.35)] overflow-hidden" style={{ borderColor: "var(--rule)" }}>
              <div className="px-6 sm:px-7 py-5 border-b flex items-center gap-3"
                style={{ borderColor: "var(--rule)", background: `radial-gradient(120% 100% at 0% 0%, ${role.color}1a 0%, transparent 60%)` }}>
                <div className="w-10 h-10 rounded-2xl grid place-items-center transition-transform duration-500 hover:rotate-[8deg] hover:scale-110"
                  style={{ background: `${role.color}1a`, color: role.color, boxShadow: `0 6px 16px -8px ${role.color}80` }}>
                  <Icon size={20} />
                </div>
                <div>
                  <div className="font-display font-bold text-base">Sign up as {view.label}</div>
                  <div className="text-xs text-muted">3 quick fields. No phone. No clutter.</div>
                </div>
              </div>

              <div className="p-6 sm:p-7">
                <button
                  type="button"
                  onClick={onGoogle}
                  data-testid="google-signup-btn"
                  className="w-full flex items-center justify-center gap-2.5 px-4 py-3.5 rounded-2xl font-semibold text-sm border-2 bg-white transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.99] hover:shadow-[0_10px_24px_-12px_rgba(0,40,168,0.35)]"
                  style={{ borderColor: role.color, color: role.color }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.5 12.2c0-.8-.1-1.6-.2-2.3H12v4.4h5.9c-.3 1.4-1 2.5-2.2 3.3v2.7h3.5c2-1.9 3.3-4.7 3.3-8.1z"/><path fill="#34A853" d="M12 23c3 0 5.5-1 7.3-2.7l-3.5-2.7c-1 .7-2.3 1.1-3.8 1.1-2.9 0-5.4-2-6.3-4.6H2.1v2.8C3.9 20.6 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.7 14.1c-.2-.7-.4-1.4-.4-2.1s.1-1.4.4-2.1V7.1H2.1C1.4 8.6 1 10.2 1 12s.4 3.4 1.1 4.9l3.6-2.8z"/><path fill="#EA4335" d="M12 5.4c1.6 0 3.1.6 4.2 1.6l3.1-3.1C17.4 2 14.9 1 12 1 7.7 1 3.9 3.4 2.1 7.1l3.6 2.8C6.6 7.4 9.1 5.4 12 5.4z"/></svg>
                  One-click Google signup
                </button>

                <div className="mt-3">
                  <LinkedInButton role={role.key} testId="linkedin-signup-btn" label="Sign up with LinkedIn" />
                </div>

                <div className="flex items-center gap-3 my-5 text-[10px] uppercase tracking-widest text-muted">
                  <span className="h-px flex-1 bg-rule" /> or email <span className="h-px flex-1 bg-rule" />
                </div>

                <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
                  <div className="group">
                    <label className="text-xs font-bold uppercase tracking-wider text-muted">{role.nameLabel}</label>
                    <div className="relative mt-1.5">
                      {role.orgRequired
                        ? <Building2 size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted group-focus-within:text-signal transition-colors pointer-events-none" />
                        : <User2     size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted group-focus-within:text-signal transition-colors pointer-events-none" />}
                      <input
                        className="input input-with-icon"
                        required autoFocus value={form.name} onChange={upd("name")}
                        onFocus={() => setShowMatches(true)}
                        onBlur={() => setTimeout(() => setShowMatches(false), 180)}
                        autoComplete="off"
                        data-testid="signup-name"
                        placeholder={view.namePlaceholder}
                      />
                      {/* Company typeahead dropdown — only for role=company, only when we have matches and no join is locked in */}
                      {role.key === "company" && !joinCompany && showMatches && companyMatches.length > 0 && (
                        <div
                          className="absolute z-20 left-0 right-0 mt-1 bg-white border rounded-xl shadow-lg overflow-hidden max-h-64 overflow-y-auto"
                          style={{ borderColor: "var(--rule)" }}
                          data-testid="signup-company-matches"
                        >
                          <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-muted border-b" style={{ borderColor: "var(--rule)" }}>
                            Already on SkillConnect — join as recruiter
                          </div>
                          {companyMatches.map((m) => (
                            <button
                              type="button"
                              key={m.company_code}
                              onMouseDown={(ev) => { ev.preventDefault(); setJoinCompany(m); setForm(f => ({ ...f, name: m.name })); setShowMatches(false); }}
                              data-testid={`signup-company-match-${m.company_code}`}
                              className="w-full text-left px-3 py-2.5 hover:bg-slate-50 transition-colors flex items-center gap-3"
                            >
                              <div className="w-8 h-8 rounded-lg grid place-items-center shrink-0" style={{ background: `${role.color}15`, color: role.color }}>
                                <Building2 size={14} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-semibold truncate">{m.name}</div>
                                <div className="text-[11px] text-muted font-mono">#{m.company_code}{m.verified ? " · verified" : ""}</div>
                              </div>
                              <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: role.color }}>Join →</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Selected-join banner */}
                    {role.key === "company" && joinCompany && (
                      <div
                        className="mt-2 flex items-start gap-2.5 px-3 py-2.5 rounded-xl border"
                        style={{ borderColor: `${role.color}40`, background: `${role.color}0f` }}
                        data-testid="signup-company-join-banner"
                      >
                        <Building2 size={16} className="mt-0.5 shrink-0" style={{ color: role.color }} />
                        <div className="min-w-0 flex-1 text-xs">
                          <div className="font-semibold">Joining <span style={{ color: role.color }}>{joinCompany.name}</span> as a new recruiter</div>
                          <div className="text-muted mt-0.5 font-mono">Company ID #{joinCompany.company_code}</div>
                        </div>
                        <button type="button"
                          onClick={() => { setJoinCompany(null); setForm(f => ({ ...f, name: "" })); }}
                          className="text-[11px] font-bold uppercase tracking-wider hover:underline"
                          style={{ color: role.color }}
                          data-testid="signup-company-join-cancel"
                        >
                          Change
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="group">
                    <label className="text-xs font-bold uppercase tracking-wider text-muted">Email</label>
                    <div className="relative mt-1.5">
                      <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted group-focus-within:text-signal transition-colors pointer-events-none" />
                      <input
                        className="input input-with-icon" required type="email" autoComplete="email"
                        value={form.email} onChange={upd("email")}
                        data-testid="signup-email"
                        placeholder="you@example.com"
                      />
                    </div>
                  </div>

                  <div className="group">
                    <label className="text-xs font-bold uppercase tracking-wider text-muted">Password</label>
                    <div className="relative mt-1.5">
                      <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted group-focus-within:text-signal transition-colors pointer-events-none" />
                      <input
                        className="input input-with-icon" required minLength={6} type="password" autoComplete="new-password"
                        value={form.password} onChange={upd("password")}
                        data-testid="signup-password"
                        placeholder="6+ characters"
                      />
                    </div>
                    <div className="text-[11px] text-muted mt-1.5 ml-1">Minimum 6 characters. You can change it anytime.</div>
                  </div>

                  {role.key === "working_pro" && (
                    <div className="space-y-3" data-testid="signup-pro-fields">
                      <div>
                        <label className="text-[10px] uppercase tracking-wider font-bold text-muted">Current role</label>
                        <input className="input mt-1" placeholder="e.g. Software Engineer"
                               value={form.current_role}
                               onChange={upd("current_role")}
                               data-testid="signup-pro-current-role"/>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] uppercase tracking-wider font-bold text-muted">Current company</label>
                          <input className="input mt-1" placeholder="e.g. Infosys"
                                 value={form.current_company}
                                 onChange={upd("current_company")}
                                 data-testid="signup-pro-current-company"/>
                        </div>
                        <div>
                          <label className="text-[10px] uppercase tracking-wider font-bold text-muted">Years exp.</label>
                          <input className="input mt-1" type="number" min={0} max={50} step="0.5"
                                 placeholder="3"
                                 value={form.years_of_experience}
                                 onChange={upd("years_of_experience")}
                                 data-testid="signup-pro-yoe"/>
                        </div>
                      </div>
                      <div className="text-[11px] text-muted ml-1">
                        Used to surface lateral roles + filter out fresher-only postings. All optional.
                      </div>
                    </div>
                  )}

                  {role.key === "institution" && (
                    <>
                      <div data-testid="signup-institution-type">
                        <label className="text-xs font-bold uppercase tracking-wider text-muted">Institution type</label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                          {INSTITUTION_TYPES.map(t => (
                            <button type="button" key={t.value} onClick={() => setInstType(t.value)}
                              className={`text-xs font-bold px-3 py-2 rounded-lg border transition ${instType === t.value ? "bg-signal text-white border-signal" : "border-rule text-muted hover:border-signal hover:text-signal"}`}
                              data-testid={`signup-inst-type-${t.value}`}>
                              {t.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      {instType !== "university" && (
                        <div data-testid="signup-affiliated">
                          <label className="text-xs font-bold uppercase tracking-wider text-muted">Affiliated University <span className="text-[10px] opacity-60">(optional)</span></label>
                          <input
                            className="input mt-1.5"
                            list="universities-list"
                            placeholder="Type or pick — e.g. Anna University"
                            value={aff}
                            onChange={e => setAff(e.target.value)}
                            data-testid="signup-aff-input"
                          />
                          <datalist id="universities-list">
                            {universities.map(u => (
                              <option key={u.id} value={u.name}>{u.short ? `${u.short} · ${u.state}` : u.state}</option>
                            ))}
                          </datalist>
                          <p className="text-[11px] text-muted mt-1 ml-1">Don&apos;t see your university? Just type the name — admin can add it later.</p>
                        </div>
                      )}
                    </>
                  )}

                  {error && (
                    <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-xl px-3 py-2 animate-[shake_0.4s_ease]" data-testid="signup-error">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit" disabled={busy}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-2xl font-bold text-sm text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_30px_-12px_rgba(0,40,168,0.55)] active:translate-y-0 active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0"
                    style={{ background: `linear-gradient(135deg, ${role.color} 0%, ${role.color}cc 100%)` }}
                    data-testid="signup-submit-btn"
                  >
                    {busy ? (
                      <>
                        <span className="inline-block w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                        Creating account…
                      </>
                    ) : (
                      <>Create {view.label} account <ArrowRight size={15} /></>
                    )}
                  </button>
                </form>

                <p className="text-center text-xs text-muted mt-4">
                  Already have an account?{" "}
                  <Link to="/login" className="font-semibold hover:underline" style={{ color: role.color }}>Log in</Link>
                </p>
              </div>
            </div>
          </div>

          {/* Right preview / why-card */}
          <aside className="lg:col-span-2">
            <div className="rounded-2xl p-7 text-white relative overflow-hidden h-full"
              style={{ background: `linear-gradient(160deg, ${role.color} 0%, #001A66 110%)` }}>
              <div className="absolute -top-16 -right-16 w-44 h-44 rounded-full blur-3xl opacity-30" style={{ background: "white" }} />
              <div className="relative">
                <span className="font-mono text-[11px] tracking-widest uppercase opacity-80">Why {view.label}s love SkillConnect</span>
                <h3 className="font-display font-black text-2xl mt-2 leading-tight">{view.tagline}</h3>
                <ul className="mt-5 space-y-3 text-sm">
                  {view.perks.map((p) => (
                    <li key={p} className="flex items-center gap-2.5">
                      <span className="w-5 h-5 rounded-full bg-white/20 grid place-items-center text-white text-[10px] font-bold">✓</span>
                      <span className="text-white/95">{p}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-5 border-t border-white/15 text-[11px] uppercase tracking-widest font-mono text-white/70">
                  Secured · JWT + Google OAuth
                </div>
              </div>
            </div>
          </aside>
        </div>
      </main>

      <PublicFooter compact />
    </div>
  );
}
